import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { ToastType } from '@components/Toast';
import {
  AddressBook,
  AddressBookModel,
  FilteredAddressBook,
  NewAddressResponse,
} from '@store/customers/customerDetails/addressBookTab';
import { handleToast } from '@utils/utils';
import { createError } from '@utils/errorUtils';
import { logError } from '@utils/errorLogging';
import isEmpty from 'lodash/isEmpty';

import { setAddressBookStore } from './store';

export type FacilityAddressValidationResponse = {
  validatedAddress?: {
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    countryCode?: string;
  };
  verdict?: {
    validationStatus?: string;
    validationMessages?: string[];
  };
  latitude?: number;
  longitude?: number;
};

export const validateFacilityAddress = async (payload: {
  address: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
}): Promise<FacilityAddressValidationResponse> => {
  try {
    return await apiClient.post('Location/ValidateAddress', payload);
  } catch (error) {
    logError('validateFacilityAddress', error as Error);
    throw createError(error, 'Address verification failed');
  }
};

export const getFilteredAddressBookData = async (
  payload: Partial<FilteredAddressBook>,
) => {
  try {
    const response: {
      addresses: AddressBookModel[];
      totalCount: number;
    } = await apiClient.get('CustomerAddress/getcustomeraddress', {
      params: payload,
    });
    return response;
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to fetch Address Book Data');
  }
};

export const postAddress = async (payload: AddressBook) => {
  try {
    const { cb, ...restPayload } = payload;
    const response: NewAddressResponse = await apiClient.post(
      'CustomerAddress',
      restPayload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.addressBookCreate,
      ]),
    );
    if (response.isSuccess) {
      cb && cb('Address Successfully Saved!');
      return response;
    }
  } catch (error) {
    handleError(error, 'Failed to save address');
  }
};

export const editAddress = async (payload: AddressBook) => {
  try {
    const { cb, ...restPayload } = payload;
    const response: AddressBookModel = await apiClient.put(
      'CustomerAddress',
      restPayload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.addressBookUpdate,
      ]),
    );
    if (!isEmpty(response)) {
      cb && cb('Address Successfully Updated!');
      return response;
    }
  } catch (error) {
    handleError(error, 'Failed to update address');
  }
};

export const uploadCustomerAddressFile = async (
  customerId: number,
  file: File,
) => {
  try {
    const formData = new FormData();
    formData.append('File', file);
    formData.append('CustomerId', customerId.toString());

    const res: V4ApiResponse<{
      failedCount: number;
      failedCsv: string; // byte[]
    }> = await apiClient.post('/CustomerAddress/uploadAddressBook', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...buildChangeTrackingRequestConfig([
          CHANGE_TRACKING_SOURCES.customerDetails.addressBookImport,
        ])?.headers,
      },
    });

    return res;
  } catch (error) {
    handleError(error, 'Uploading customer addresses failed');
    throw error;
  }
};

export const downloadSampleCustomerAddressFile = async () => {
  try {
    const res: V4ApiResponse<string> = await apiClient.get(
      '/CustomerAddress/sampleAddressBook',
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );

    return res.value;
  } catch (error) {
    handleError(error, 'Downloading sample customer addresses failed');
    throw error;
  }
};

const handleError = (error: unknown, toastMessage: string) => {
  if (Array.isArray(error)) {
    const errorMessages = error.map((v) => {
      if (typeof v === 'string') {
        return v;
      } else if (typeof v === 'object' && v !== null && 'errorMessage' in v) {
        return (v as { errorMessage: string }).errorMessage;
      }
      return 'Unspecified/unknown error';
    });
    setAddressBookStore('addOrEditErrorMessage', errorMessages.join(', '));
  } else {
    throw new Error(toastMessage);
  }
  handleToast(ToastType.Error, toastMessage);
};

export const deleteAddressBook = async (addressId: string) => {
  try {
    const res: { isSuccess: boolean } = await apiClient.delete(
      `CustomerAddress/${addressId}`,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.addressBookDelete,
      ]),
    );
    if (res.isSuccess) {
      handleToast(ToastType.Success, 'Address Successfully Deleted!');
      return res.isSuccess;
    }
  } catch (error: unknown) {
    throw new Error(`Failed to delete address: ${error}`);
  }
};

export const fetchAddressBookById = async (addressId: string) => {
  try {
    const res: AddressBookModel = await apiClient.get(
      `CustomerAddress/getaddressbyid/${addressId}`,
    );
    return res;
  } catch (error: unknown) {
    throw createError(error, 'Failed to load address details');
  }
};
